import React, { useState } from "react";
import { Content } from "antd/es/layout/layout";
import { Avatar, Button, message } from "antd";
import { LeftOutlined, SendOutlined, DeleteOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import * as mutations from "../graphql/mutations";
import "./style.css";
import AppActionModal from "../Components/AppActionModal";

const RecipientDetail = () => {
  const API = generateClient();
  const navigate = useNavigate();
  const { state } = useLocation();
  const recipient = state?.recipient;
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const formatPhoneNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    if (!digits) return "N/A";
    if (digits.startsWith("1246") && digits.length === 11) {
      const local = digits.slice(4);
      return `+1 246 ${local.slice(0, 3)}-${local.slice(3)}`;
    }
    if (digits.startsWith("1") && digits.length === 11) {
      const local = digits.slice(1);
      return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return value || "N/A";
  };

  if (!recipient) {
    return (
      <Content className="appPageContent">
        <div className="appPageShell">
          <div className="appSectionHeader">
            <h2 className="appSectionTitle">Recipient Details</h2>
          </div>
          <div className="appTableCard">
            <p>Recipient data is not available.</p>
            <Button type="primary" onClick={() => navigate("/recipients")}>
              Back to Recipients
            </Button>
          </div>
        </div>
      </Content>
    );
  }

  const handleDeleteRecipient = async () => {
    try {
      setDeleting(true);
      await API.graphql({
        query: mutations.deleteRecipient,
        variables: { input: { id: recipient.id } },
      });
      message.success("Recipient deleted.");
      navigate("/recipients");
    } catch (error) {
      console.error("Error deleting recipient:", error);
      message.error("Unable to delete recipient. Please try again.");
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <Content className="appPageContent">
      <div className="appPageShell recipientDetailShell">
        <div className="recipientDetailHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate("/recipients")}
            className="recipientDetailBackBtn"
          />
          <h2 className="appSectionTitle">Recipient Details</h2>
        </div>

        <div className="appTableCard recipientDetailCard">
          <div className="recipientDetailTop">
            <Avatar
              size={64}
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${recipient.firstName} ${recipient.lastName}`}
              className="recipientDetailAvatar"
            />
            <div className="recipientDetailIdentity">
              <div className="recipientDetailName">
                {recipient.firstName} {recipient.lastName}
              </div>
              <div className="recipientDetailType">
                {recipient.typeOfRecipient || "Personal"}
              </div>
            </div>
          </div>

          <div className="recipientDetailRow">
            <span>Email</span>
            <strong>{recipient.email || "N/A"}</strong>
          </div>
          <div className="recipientDetailRow">
            <span>Phone</span>
            <strong>{formatPhoneNumber(recipient.phoneNumber)}</strong>
          </div>
          <div className="recipientDetailRow">
            <span>Address</span>
            <strong>
              {[
                recipient.addressStreet,
                recipient.addressCity,
                recipient.addressState,
                recipient.addressCountry,
                recipient.addressZipCode,
              ]
                .filter(Boolean)
                .join(", ") || "N/A"}
            </strong>
          </div>
          <div className="recipientDetailRow">
            <span>Delivery Method</span>
            <strong>{recipient.recipientDeliveryMethod || "N/A"}</strong>
          </div>

          {recipient.recipientDeliveryMethod === "Bank Transfer" ? (
            <>
              <div className="recipientDetailRow">
                <span>Bank Name</span>
                <strong>{recipient.bankName || "N/A"}</strong>
              </div>
              <div className="recipientDetailRow">
                <span>Branch</span>
                <strong>{recipient.bankBranch || "N/A"}</strong>
              </div>
              <div className="recipientDetailRow">
                <span>Account Holder</span>
                <strong>{recipient.bankAccountHolderName || "N/A"}</strong>
              </div>
              <div className="recipientDetailRow">
                <span>Account Number</span>
                <strong>{recipient.bankAccountNumber || "N/A"}</strong>
              </div>
            </>
          ) : null}

          <div className="recipientDetailActions">
            <Button
              size="large"
              danger
              icon={<DeleteOutlined />}
              className="recipientDetailActionBtn recipientDetailDeleteBtn"
              loading={deleting}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
            <Button
              type="primary"
              size="large"
              className="signupbtn recipientDetailActionBtn recipientDetailSendBtn"
              icon={<SendOutlined />}
              onClick={() => navigate("/sendMoney", { state: { recipient } })}
            >
              Send Money
            </Button>
          </div>
        </div>
      </div>

      <AppActionModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        variant="error"
        icon={<DeleteOutlined />}
        title="Delete Recipient"
        description="Are you sure you want to delete this recipient?"
        maskClosable={!deleting}
        closable={!deleting}
        secondaryAction={{
          label: "Cancel",
          onClick: () => setDeleteModalOpen(false),
          disabled: deleting,
        }}
        primaryAction={{
          label: "Delete",
          onClick: handleDeleteRecipient,
          loading: deleting,
          danger: true,
          className: "recipientDetailDeleteConfirmBtn",
        }}
      />
    </Content>
  );
};

export default RecipientDetail;
